/* P.Louise — Recommendations (Rebuy API) carousel
   Cards are fetched from the Rebuy custom data-source API at runtime (instead of
   a Shopify collection), then rendered into the same candy carousel design.
   Carousel scroll + arrow enable/disable, staggered entrance, AJAX add-to-cart
   with an "added to bag" toast. Each section instance is wired independently. */
(function () {
  /* ---- small helpers ----------------------------------------------------- */
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* only allow same-origin paths or http(s) absolute URLs as href/src */
  function safeUrl(url) {
    var u = String(url == null ? "" : url).trim();
    if (!u) return "";
    if (u.charAt(0) === "/") return u;
    if (/^https?:\/\//i.test(u)) return u;
    return "";
  }

  /* Format a price. When a Storefront-API currency code is supplied (the
     shopper's Markets presentment currency), use Intl for a correctly localised
     amount + symbol; otherwise fall back to the section's plain symbol prefix
     applied to the raw Rebuy amount. */
  function formatMoney(opts, value, currencyCode) {
    var n = parseFloat(value);
    if (isNaN(n)) return "";
    if (currencyCode) {
      try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: currencyCode }).format(n);
      } catch (e) { /* unknown/unsupported currency code — fall through to symbol */ }
    }
    return opts.currency + n.toFixed(2);
  }

  /* ---- Rebuy product field accessors (defensive about field names) ------- */
  function productUrl(p) {
    var url = safeUrl(p.url);
    if (url) return url;
    if (p.handle) return "/products/" + encodeURIComponent(p.handle);
    return "#";
  }

  function productImage(p) {
    var candidates = [
      p.image && p.image.src,
      typeof p.featured_image === "string" ? p.featured_image : p.featured_image && p.featured_image.src,
      p.images && p.images[0] && (typeof p.images[0] === "string" ? p.images[0] : p.images[0].src)
    ];
    for (var i = 0; i < candidates.length; i++) {
      var u = safeUrl(candidates[i]);
      if (u) return u;
    }
    return "";
  }

  /* first sellable variant (falls back to first variant). Treats a variant as
     sellable unless it explicitly says available:false, since the Rebuy
     (Shopify REST-style) payload often omits an availability flag entirely. */
  function pickVariant(p) {
    var variants = p.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && variants[i].available !== false) return variants[i];
    }
    return variants[0] || null;
  }

  function priceInfo(p) {
    /* Prefer the presentment price fetched from Shopify's Storefront API in the
       shopper's market (attached as p.__price by fetchStorefront) so the amount
       AND currency follow Shopify Markets. Rebuy only ever returns the store's
       base currency, so fall back to it (with the section symbol) otherwise. */
    var sf = p.__price;
    if (sf && sf.amount != null) {
      return { value: sf.amount, currencyCode: sf.currencyCode || null, varies: !!sf.varies };
    }
    var variant = pickVariant(p);
    var min = p.price_min != null ? p.price_min : p.price;
    var max = p.price_max != null ? p.price_max : p.price;
    var value = min != null ? min : (variant ? variant.price : null);
    var varies = min != null && max != null && String(min) !== String(max);
    return { value: value, currencyCode: null, varies: varies };
  }

  function isAvailable(p) {
    if (p.available === false) return false;
    if (p.available === true) return true;
    var variant = pickVariant(p);
    if (variant && variant.available === false) return false;
    /* the payload doesn't say — assume sellable so single-variant products can
       still offer Add to Cart rather than always falling back to a link. */
    return true;
  }

  /* normalise tags: Shopify REST payloads give a comma-separated string,
     Storefront/other shapes give an array — accept either. */
  function tagList(p) {
    var raw = p.tags;
    var list = Array.isArray(raw) ? raw : String(raw == null ? "" : raw).split(",");
    return list.map(function (t) { return String(t).trim().toLowerCase(); }).filter(Boolean);
  }

  /* Badges are configured globally in Theme settings → Product badges (the
     custom tag groups). Each group contributes one or more tag names plus a
     background/text colour; a product shows a badge for every group tag it
     carries, styled with that group's colours + the theme's shape/case/weight. */
  function badgesFor(p, opts) {
    var defs = opts.badgeDefs;
    if (!defs || !defs.length) return "";
    var tags = tagList(p);
    var radius = badgeRadius(opts.badgeShape);
    var weight = opts.badgeBold ? "700" : "500";
    var transform = opts.badgeCase === "upper" ? "uppercase"
                  : opts.badgeCase === "capitalize" ? "capitalize" : "none";
    var seen = {}, html = "";
    defs.forEach(function (def) {
      var key = def.name.toLowerCase();
      if (seen[key] || tags.indexOf(key) === -1) return;
      seen[key] = 1;
      var style =
        "background:" + esc(def.bg) + ";color:" + esc(def.fg) +
        ";border-radius:" + radius + ";font-weight:" + weight +
        ";text-transform:" + transform + ";";
      html += '<span class="rec-badge" style="' + style + '">' + esc(def.name) + "</span>";
    });
    return html ? '<div class="rec-badges">' + html + "</div>" : "";
  }

  function badgeRadius(shape) {
    if (shape === "pill") return "999px";
    if (shape === "rounded_rectangle") return "8px";
    return "2px"; /* rectangle */
  }

  /* Flatten the Theme-settings badge groups (passed as JSON:
     [{tags:"A, B", bg:"#..", fg:"#.."}, ...]) into per-tag defs
     [{name, bg, fg}] that badgesFor matches against product tags. */
  function parseBadgeGroups(raw) {
    var groups;
    try { groups = JSON.parse(raw || "[]"); } catch (e) { groups = []; }
    if (!Array.isArray(groups)) return [];
    var defs = [];
    groups.forEach(function (g) {
      if (!g || typeof g !== "object") return;
      var bg = typeof g.bg === "string" ? g.bg : "";
      var fg = typeof g.fg === "string" ? g.fg : "";
      String(g.tags == null ? "" : g.tags).split(",").forEach(function (name) {
        name = name.trim();
        if (name) defs.push({ name: name, bg: bg, fg: fg });
      });
    });
    return defs;
  }

  /* ---- review rating (Shopify standard reviews.rating metafield) --------- */
  /* Read a metafield value tolerating the shapes a Rebuy payload may use:
       array  [{namespace,key,value}]   (Shopify REST style)
       object {namespace: {key: value}} (Liquid/Storefront style)
       flat   {"namespace.key": value} */
  function getMetafield(p, namespace, key) {
    var mf = p.metafields;
    if (!mf) return null;
    if (Array.isArray(mf)) {
      for (var i = 0; i < mf.length; i++) {
        if (mf[i] && mf[i].namespace === namespace && mf[i].key === key) return mf[i].value;
      }
      return null;
    }
    if (typeof mf === "object") {
      var ns = mf[namespace];
      if (ns && typeof ns === "object" && ns[key] != null) {
        var v = ns[key];
        return v && typeof v === "object" && "value" in v ? v.value : v;
      }
      if (mf[namespace + "." + key] != null) return mf[namespace + "." + key];
    }
    return null;
  }

  /* Turn a raw reviews.rating value (+ optional rating_count) into
     { rating, scaleMax, count } or null. reviews.rating is a JSON string like
     {"scale_min":"1.0","scale_max":"5.0","value":"4.6"}; the count is a plain
     integer string. Shared by the Rebuy-payload and Storefront-API paths. */
  function parseReview(ratingRaw, countRaw) {
    if (ratingRaw == null) return null;

    var rating = null, scaleMax = 5, obj = null;
    if (typeof ratingRaw === "object") obj = ratingRaw;
    else if (typeof ratingRaw === "string" && ratingRaw.trim().charAt(0) === "{") {
      try { obj = JSON.parse(ratingRaw); } catch (e) {}
    }
    if (obj) {
      rating = parseFloat(obj.value);
      if (obj.scale_max != null && parseFloat(obj.scale_max)) scaleMax = parseFloat(obj.scale_max);
    } else {
      rating = parseFloat(ratingRaw);
    }
    if (rating == null || isNaN(rating)) return null;

    var count = countRaw != null ? parseInt(countRaw, 10) : null;
    return { rating: rating, scaleMax: scaleMax, count: count != null && !isNaN(count) ? count : null };
  }

  /* Rating from metafields embedded in the Rebuy payload (the fallback path,
     used when no Storefront token is configured or the lookup is unavailable). */
  function reviewInfo(p) {
    return parseReview(getMetafield(p, "reviews", "rating"), getMetafield(p, "reviews", "rating_count"));
  }

  function reviewHtml(p) {
    /* Prefer a rating fetched live from Shopify's Storefront API (attached as
       p.__review by fetchStorefront); fall back to the Rebuy payload metafields. */
    var r = p.__review || reviewInfo(p);
    if (!r) return "";
    var pct = Math.max(0, Math.min(100, (r.rating / r.scaleMax) * 100));
    var num = r.rating.toFixed(1);
    var label = "Rated " + num + " out of 5" + (r.count != null ? " based on " + r.count + " reviews" : "");
    return (
      '<span class="rec-rating" aria-label="' + esc(label) + '">' +
        '<span class="rec-stars" aria-hidden="true"><span class="rec-stars__fill" style="width:' + pct + '%"></span></span>' +
        '<span class="rec-rating__num">' + esc(num) + "</span>" +
        (r.count != null ? '<span class="rec-rating__count">(' + r.count + ")</span>" : "") +
      "</span>"
    );
  }

  /* ---- card markup ------------------------------------------------------- */
  function cardHtml(p, idx, opts) {
    var url = productUrl(p);
    var title = p.title || "Product";
    var img = productImage(p);
    var price = priceInfo(p);
    var variant = pickVariant(p);
    var available = isAvailable(p);
    var canAdd = available && (p.variants || []).length === 1 && variant && variant.id != null;

    var media =
      '<div class="rec-media">' +
        '<a href="' + esc(url) + '" aria-label="' + esc(title) + '" tabindex="-1">' +
          (img
            ? '<img src="' + esc(img) + '" alt="' + esc(title) + '" loading="lazy">'
            : "") +
        "</a>" +
        badgesFor(p, opts) +
      "</div>";

    var priceHtml =
      '<span class="rec-price">' +
        (price.varies ? '<span class="rec-price__from">From</span>' : "") +
        '<span class="rec-price__now">' + esc(formatMoney(opts, price.value, price.currencyCode)) + "</span>" +
      "</span>";

    var cta;
    if (canAdd) {
      cta =
        '<form data-rba-add data-product-title="' + esc(title) + '" action="/cart/add" method="post" enctype="multipart/form-data">' +
          '<input type="hidden" name="id" value="' + esc(variant.id) + '">' +
          '<input type="hidden" name="quantity" value="1">' +
          '<button class="rec-btn rec-btn--primary" type="submit">' + esc(opts.addLabel) + "</button>" +
        "</form>";
    } else {
      cta =
        '<a class="rec-btn rec-btn--inverse" href="' + esc(url) + '">' +
          esc(available ? opts.optionsLabel : opts.soldoutLabel) +
        "</a>";
    }

    return (
      '<li class="rec-card" style="--i: ' + idx + ';">' +
        media +
        '<div class="rec-overlay">' +
          '<h3 class="rec-name"><a href="' + esc(url) + '">' + esc(title) + "</a></h3>" +
          '<div class="rec-meta">' + priceHtml + (opts.showReviews ? reviewHtml(p) : "") + "</div>" +
          '<div class="rec-cta">' + cta + "</div>" +
        "</div>" +
      "</li>"
    );
  }

  function skeletonHtml(count) {
    var card =
      '<li class="rec-card rec-card--skeleton" aria-hidden="true">' +
        '<div class="rec-media"></div>' +
        '<div class="rec-overlay">' +
          '<div class="rec-skel-line"></div>' +
          '<div class="rec-skel-line rec-skel-line--short"></div>' +
        "</div>" +
      "</li>";
    var html = "";
    for (var i = 0; i < count; i++) html += card;
    return html;
  }

  /* ---- carousel + toast wiring (runs after cards are in the DOM) --------- */
  function wireCarousel(root) {
    var track = root.querySelector("[data-rba-track]");
    var prevBtn = root.querySelector("[data-rba-prev]");
    var nextBtn = root.querySelector("[data-rba-next]");
    if (!track) return;

    requestAnimationFrame(function () { track.classList.add("animate"); });

    function updateArrows() {
      if (!prevBtn || !nextBtn) return;
      var maxScroll = track.scrollWidth - track.clientWidth;
      prevBtn.disabled = track.scrollLeft <= 4;
      nextBtn.disabled = track.scrollLeft >= maxScroll - 4;
    }

    function scrollByCard(dir) {
      var card = track.querySelector(".rec-card");
      var step = card ? card.offsetWidth + 12 : 240;
      track.scrollBy({ left: dir * step * 1.5, behavior: "smooth" });
    }

    if (prevBtn) prevBtn.addEventListener("click", function () { scrollByCard(-1); });
    if (nextBtn) nextBtn.addEventListener("click", function () { scrollByCard(1); });
    track.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    updateArrows();
  }

  function wireToastAndCart(root) {
    var toast = root.querySelector("[data-rba-toast]");
    var toastText = root.querySelector("[data-rba-toast-text]");
    var toastTimer = null;

    function showToast(message) {
      if (!toast || !toastText) return;
      toastText.textContent = message;
      toast.classList.remove("show");
      void toast.offsetWidth; /* reflow so the animation restarts */
      toast.classList.add("show");
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { toast.classList.remove("show"); }, 2600);
    }

    function truncate(name) {
      return name.length > 32 ? name.slice(0, 30) + "…" : name;
    }

    /* AJAX add-to-cart, delegated so it works for cards injected later */
    root.addEventListener("submit", function (e) {
      var form = e.target.closest("[data-rba-add]");
      if (!form) return;
      e.preventDefault();

      var btn = form.querySelector("button[type=submit]");
      var name = form.getAttribute("data-product-title") || "item";
      if (btn) btn.disabled = true;

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: new FormData(form)
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error((res.data && res.data.description) || "Add to cart failed");
          showToast("Added to bag — " + truncate(name));
          document.dispatchEvent(new CustomEvent("rec:item-added", { detail: res.data, bubbles: true }));
        })
        .catch(function () {
          showToast("Couldn't add — " + truncate(name));
        })
        .finally(function () { if (btn) btn.disabled = false; });
    });
  }

  /* ---- render + fetch ---------------------------------------------------- */
  function render(root, products, opts) {
    var track = root.querySelector("[data-rba-track]");
    if (!track) return;

    if (!products.length) {
      track.innerHTML = '<li class="rec-empty">' + esc(opts.emptyText) + "</li>";
      return;
    }

    track.innerHTML = products
      .slice(0, opts.limit)
      .map(function (p, i) { return cardHtml(p, i, opts); })
      .join("");

    wireCarousel(root);
  }

  /* Find the products array in the Rebuy response, tolerating wrapper shapes:
     [...]  |  {data:[...]}  |  {products:[...]}  |  {data:{products:[...]}}  |
     {data:{items:[...]}}  |  {results:[...]}  etc. */
  function extractProducts(json) {
    if (Array.isArray(json)) return json;
    if (!json || typeof json !== "object") return null;
    var direct = json.data || json.products || json.items || json.results;
    if (Array.isArray(direct)) return direct;
    if (direct && typeof direct === "object") {
      var nested = direct.products || direct.items || direct.data || direct.results;
      if (Array.isArray(nested)) return nested;
    }
    return null;
  }

  /* de-dupe a list of ids into an array of non-empty strings, order preserved */
  function uniqueIds(values) {
    var ids = [];
    (values || []).forEach(function (v) {
      if (v == null || v === "") return;
      v = String(v);
      if (ids.indexOf(v) === -1) ids.push(v);
    });
    return ids;
  }

  /* Build the Rebuy request URL, layering shopper context onto the configured
     endpoint so a rule-based data source can personalise the result:
       - shopify_product_ids: the product being viewed + everything in the cart
       - shopify_variant_ids: the selected variant + the cart's variants
       - shopify_customer_id: the logged-in customer (omitted for guests)
       - limit:               how many cards we want
     Existing query params on the endpoint (e.g. ?key=) are preserved.
     Generic-feed mode sends NO context: the endpoint is called exactly as
     configured, so a standalone homepage feed returns the same list for
     everyone (passing context can make such a data source return nothing). */
  function buildEndpoint(opts, cart) {
    if (opts.generic) return opts.endpoint;

    var url;
    try { url = new URL(opts.endpoint, window.location.origin); }
    catch (e) { return opts.endpoint; }

    var productIds = uniqueIds([opts.productId].concat(cart.productIds || []));
    var variantIds = uniqueIds([opts.variantId].concat(cart.variantIds || []));

    if (productIds.length) url.searchParams.set("shopify_product_ids", productIds.join(","));
    if (variantIds.length) url.searchParams.set("shopify_variant_ids", variantIds.join(","));
    if (opts.customerId) url.searchParams.set("shopify_customer_id", opts.customerId);
    if (opts.limit) url.searchParams.set("limit", opts.limit);
    return url.toString();
  }

  /* Current cart's product + variant ids (for cart-aware recommendations).
     Resolves to empty lists on any failure so it never blocks the request. */
  function getCartContext() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return { productIds: [], variantIds: [] };
        return {
          productIds: cart.items.map(function (it) { return it.product_id; }).filter(Boolean),
          variantIds: cart.items.map(function (it) { return it.variant_id; }).filter(Boolean)
        };
      })
      .catch(function () { return { productIds: [], variantIds: [] }; });
  }

  /* Resolve a Rebuy product to its Shopify Product GID (gid://shopify/Product/N),
     the id the Storefront API expects. Rebuy's REST-style payload carries
     `admin_graphql_api_id` directly; otherwise build it from the numeric id. */
  function productGid(p) {
    var gid = p.admin_graphql_api_id;
    if (typeof gid === "string" && gid.indexOf("gid://shopify/Product/") === 0) return gid;
    var id = p.product_id != null ? p.product_id : p.id;
    if (id == null) return null;
    id = String(id);
    if (id.indexOf("gid://") === 0) return id;
    if (/^\d+$/.test(id)) return "gid://shopify/Product/" + id;
    return null;
  }

  /* A valid Storefront API date version (YYYY-MM), else the readOpts default. */
  function apiVersion(v) {
    return /^\d{4}-\d{2}$/.test(v || "") ? v : "2025-10";
  }

  /* A shopper country as a 2-letter ISO code (for the @inContext directive that
     drives Markets presentment pricing), or "" if unknown/invalid. */
  function contextCountry(v) {
    return /^[A-Za-z]{2}$/.test(v || "") ? v.toUpperCase() : "";
  }

  /* One batched Storefront API request for the whole carousel that pulls, per
     product: the presentment price range (in the shopper's Shopify Market via
     the @inContext directive) and the reviews.rating / reviews.rating_count
     metafields. Results are attached onto each product as p.__price and
     p.__review. Resolves to a no-op when there's no token, no resolvable ids, or
     the request fails — so render still proceeds, prices fall back to the Rebuy
     amount + section symbol, and ratings fall back to the Rebuy payload. */
  function fetchStorefront(products, opts) {
    if (!opts.storefrontToken) return Promise.resolve();

    var byGid = {}, gids = [];
    products.slice(0, opts.limit).forEach(function (p) {
      var gid = productGid(p);
      if (!gid) return;
      if (!byGid[gid]) { byGid[gid] = []; gids.push(gid); }
      byGid[gid].push(p);
    });
    if (!gids.length) return Promise.resolve();

    /* @inContext(country: XX) makes Shopify return prices in that market's
       presentment currency; omitted when we don't know the country, so the
       store's default currency is returned. */
    var country = contextCountry(opts.country);
    var context = country ? " @inContext(country: " + country + ")" : "";
    var query =
      "query recs($ids:[ID!]!)" + context + "{nodes(ids:$ids){... on Product{" +
        "id " +
        "priceRange{minVariantPrice{amount currencyCode} maxVariantPrice{amount currencyCode}} " +
        'rating:metafield(namespace:"reviews",key:"rating"){value} ' +
        'ratingCount:metafield(namespace:"reviews",key:"rating_count"){value}' +
      "}}}";
    var url = "/api/" + apiVersion(opts.storefrontApiVersion) + "/graphql.json";

    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-Shopify-Storefront-Access-Token": opts.storefrontToken
      },
      body: JSON.stringify({ query: query, variables: { ids: gids } })
    })
      .then(function (r) {
        if (!r.ok) throw new Error("Storefront API returned HTTP " + r.status + " " + r.statusText);
        return r.json();
      })
      .then(function (json) {
        if (json && json.errors && json.errors.length) {
          throw new Error("Storefront API errors: " + JSON.stringify(json.errors));
        }
        var nodes = json && json.data && json.data.nodes;
        if (!Array.isArray(nodes)) return;
        nodes.forEach(function (n) {
          if (!n || !n.id || !byGid[n.id]) return;
          var pr = n.priceRange || {};
          var minP = pr.minVariantPrice, maxP = pr.maxVariantPrice;
          var price = minP && minP.amount != null
            ? {
                amount: minP.amount,
                currencyCode: minP.currencyCode || null,
                varies: !!(maxP && maxP.amount != null && parseFloat(maxP.amount) !== parseFloat(minP.amount))
              }
            : null;
          var review = parseReview(n.rating && n.rating.value, n.ratingCount && n.ratingCount.value);
          byGid[n.id].forEach(function (p) {
            if (price) p.__price = price;
            if (review) p.__review = review;
          });
        });
      });
  }

  function fetchProducts(root, opts) {
    var track = root.querySelector("[data-rba-track]");
    if (track) track.innerHTML = skeletonHtml(Math.min(opts.limit, 4));
    var log = window.console || { warn: function () {}, info: function () {} };
    var tag = "[recommendations-rebuy-api]";

    if (!opts.endpoint) {
      log.warn && log.warn(tag, "No Rebuy endpoint configured on the section.");
      render(root, [], opts);
      return;
    }

    var emptyCart = { productIds: [], variantIds: [] };
    var cartReady = opts.useCart && !opts.generic ? getCartContext() : Promise.resolve(emptyCart);

    cartReady
      .then(function (cart) {
        return fetch(buildEndpoint(opts, cart), { headers: { Accept: "application/json" }, credentials: "omit" });
      })
      .then(function (r) {
        if (!r.ok) throw new Error("Rebuy API returned HTTP " + r.status + " " + r.statusText);
        return r.json();
      })
      .then(function (json) {
        var products = extractProducts(json);
        if (!products) {
          log.warn && log.warn(tag, "Couldn't find a products array in the Rebuy response. " +
            "Top-level keys were:", json && typeof json === "object" ? Object.keys(json) : json,
            "— full payload:", json);
          render(root, [], opts);
          return;
        }
        if (!products.length) {
          log.info && log.info(tag, "Rebuy returned 0 products for this data source/context.");
          render(root, products, opts);
          return;
        }
        if (!opts.storefrontToken) {
          render(root, products, opts);
          return;
        }
        /* Enrich with live Shopify data (Markets presentment prices + ratings,
           via the Storefront API) before rendering; never let a lookup failure
           block the cards from showing. */
        return fetchStorefront(products, opts)
          .catch(function (err) {
            log.warn && log.warn(tag, "Storefront lookup failed:",
              err && err.message ? err.message : err,
              "— rendering with Rebuy base-currency prices and any rating in the Rebuy payload.");
          })
          .then(function () { render(root, products, opts); });
      })
      .catch(function (err) {
        /* A bare "TypeError: Failed to fetch" here almost always means the
           request was blocked by CORS (Rebuy didn't allow this origin) or the
           network — check the Network tab for the request to rebuyengine.com. */
        log.warn && log.warn(tag, "Rebuy request failed:", err && err.message ? err.message : err,
          "— if this is 'Failed to fetch', it is most likely a CORS/network block. " +
          "Open the Network tab and inspect the request to the endpoint.");
        render(root, [], opts);
      });
  }

  function readOpts(root) {
    return {
      endpoint: root.getAttribute("data-rba-endpoint") || "",
      limit: parseInt(root.getAttribute("data-rba-limit"), 10) || 12,
      currency: root.getAttribute("data-rba-currency") || "£",
      addLabel: root.getAttribute("data-rba-add-label") || "Add to Cart",
      optionsLabel: root.getAttribute("data-rba-options-label") || "Choose options",
      soldoutLabel: root.getAttribute("data-rba-soldout-label") || "View product",
      emptyText: root.getAttribute("data-rba-empty-text") || "No recommendations right now — check back soon.",
      badgeDefs: parseBadgeGroups(root.getAttribute("data-rba-badge-groups")),
      badgeShape: root.getAttribute("data-rba-badge-shape") || "pill",
      badgeCase: root.getAttribute("data-rba-badge-case") || "none",
      badgeBold: root.getAttribute("data-rba-badge-bold") !== "0",
      productId: root.getAttribute("data-rba-product-id") || "",
      variantId: root.getAttribute("data-rba-variant-id") || "",
      customerId: root.getAttribute("data-rba-customer-id") || "",
      useCart: root.getAttribute("data-rba-use-cart") === "1",
      generic: root.getAttribute("data-rba-generic") === "1",
      showReviews: root.getAttribute("data-rba-reviews") !== "0",
      storefrontToken: root.getAttribute("data-rba-sf-token") || "",
      storefrontApiVersion: root.getAttribute("data-rba-sf-version") || "2025-10",
      country: root.getAttribute("data-rba-country") || ""
    };
  }

  function initSection(root) {
    if (!root || root.dataset.rbaInit === "1") return;
    root.dataset.rbaInit = "1";

    wireToastAndCart(root);
    fetchProducts(root, readOpts(root));
  }

  function initAll() {
    document.querySelectorAll("[data-rba-root]").forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  /* Theme editor: re-init when a section is loaded/reordered */
  document.addEventListener("shopify:section:load", function (e) {
    var root = e.target.querySelector("[data-rba-root]");
    if (root) initSection(root);
  });
})();